import { Button } from '@/Components/UI/Button/Button';
import Icon from '@/Components/UI/Icon/Icon';
import PopoverV2 from '@/Components/UI/Popover/PopoverV2';
import { userDataSliceName } from '@/redux/constants';
import { showModal } from '@/redux/reducers/modalsSlice';
import { MODAL_CONFIRM_LEAVE_TEAM } from '@/Utils/constants';
import clsx from 'clsx';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTable } from 'react-table';

const MEMBER = 'member';
const ADMINISTRATOR = 'administrator';

const TeamMemberActions = ({ currentUserEmail, memberEmail }) => {
  const dispatch = useDispatch();

  if (currentUserEmail !== memberEmail) return null;

  const handleRemoveFromTeamClick = () => {
    dispatch(showModal({ name: MODAL_CONFIRM_LEAVE_TEAM }));
  };

  return (
    <PopoverV2
      content={
        <div className='w-64 pt-1 rounded text-sm font-normal text-ui'>
          <ul>
            {currentUserEmail === memberEmail && (
              <li>
                <Button
                  className='!w-full !rounded-none !justify-start'
                  variant='ghost'
                  onClick={handleRemoveFromTeamClick}
                >
                  Remove from team
                </Button>
              </li>
            )}
          </ul>
        </div>
      }
    >
      <Button variant='ghost'>
        <Icon name='dots-three-vertical' size={16} />
      </Button>
    </PopoverV2>
  );
};

const TeamMemberTable = ({ teamMembers }) => {
  const { email, userProperties } = useSelector((state) => state[userDataSliceName].user);
  const { isOrgAdministrator } = userProperties;
  const [members, setMembers] = useState([]);

  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
        Cell: ({ value }) => {
          return (
            <div className='flex justify-start items-center'>
              <div className='flex items-center justify-center w-6 h-6 pt-1 text-xs font-bold text-white uppercase rounded text-items-center bg-ocean-blue-700 align-center mr-4'>
                {value?.[0]}
              </div>
              <div className='truncate w-72' title={value}>
                {value}
              </div>
            </div>
          );
        },
      },
      {
        Header: 'Role',
        accessor: 'role',
        Cell: ({ value }) => <div className='first-letter:uppercase'>{value}</div>,
      },
      {
        Header: '',
        accessor: 'name',
        id: 'rowProperty',
        Cell: ({ value }) => (
          <TeamMemberActions
            isOrgAdministrator={isOrgAdministrator}
            currentUserEmail={email}
            memberEmail={value}
          />
        ),
      },
    ],
    [members]
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    columns,
    data: members,
  });

  const setColumnWith = (col) => {
    switch (col) {
      case 'name':
        return 'min-w-44 !max-w-1/2';
      case 'status':
        return '!w-24 !max-w-24';
      case 'role':
        return '!min-w-24';
      default:
        return 'w-12';
    }
  };

  useEffect(() => {
    const updatedTeamMembers = teamMembers.map((member) => {
      return { name: member, role: MEMBER };
    });
    const currentUser = { name: email, role: isOrgAdministrator ? ADMINISTRATOR : MEMBER };

    setMembers([currentUser, ...updatedTeamMembers]);
  }, [teamMembers]);

  return (
    <div className='max-h-72 overflow-auto'>
      <table {...getTableProps()} data-cy='team-member-table' className='w-full '>
        <thead className='sticky top-0 z-1 !bg-white'>
          {headerGroups.map((headerGroup) => {
            const { key, ...restHeaderGroupProps } = headerGroup.getHeaderGroupProps();
            return (
              <tr key={key} {...restHeaderGroupProps}>
                {headerGroup.headers.map((column, i) => (
                  <th
                    key={i}
                    className={clsx(
                      'text-ui text-xs font-semibold border-b border-ui-300 h-12 text-left p-3 truncate',
                      setColumnWith(column.id)
                    )}
                  >
                    <div className='flex items-center gap-2'>{column.render('Header')}</div>
                  </th>
                ))}
              </tr>
            );
          })}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.length
            ? rows.map((row) => {
                prepareRow(row);
                const { key, ...restRowProps } = row.getRowProps();
                return (
                  <tr key={key} {...restRowProps}>
                    {row.cells.map((cell) => {
                      const { key, ...restCellProps } = cell.getCellProps();
                      return (
                        <td
                          key={key}
                          {...restCellProps}
                          className='h-14 px-3 py-1 text-xs font-normal truncate border-b text-ui border-ui-300'
                        >
                          {cell.render('Cell')}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            : null}
        </tbody>
      </table>
    </div>
  );
};

export default TeamMemberTable;
